import axios from "axios";
import Swal from "sweetalert2";
import { KeyTokenName } from "../../constants";
import { objectToFormData } from "../../helpers";

// Add a global interceptor to handle 401 and 402 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      localStorage.removeItem(KeyTokenName);
      if (currentPath !== "/login") {
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      }
    } else if (error.response?.status === 402) {
      const isDark = localStorage.getItem("appMode") === "dark";
      const bgColor = isDark ? "#1E293B" : "#FFFFFF";
      const titleColor = isDark ? "#FFFFFF" : "#1E293B";
      const textColor = isDark ? "#94A3B8" : "#475569";
      const infoBg = isDark ? "#0F172A" : "#F8FAFC";
      const infoBorder = isDark ? "#334155" : "#F1F5F9";
      const infoTextColor = isDark ? "#E2E8F0" : "#64748B";
      const cancelBgColor = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";
      const cancelTextColor = isDark ? "#E2E8F0" : "#475569";

      Swal.fire({
        title: "Ồ! Hết hạn mức học tập mất rồi",
        background: bgColor,
        html: `
          <style>
            .swal-premium-popup {
              border-radius: 24px !important;
              padding: 24px !important;
              font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
            }
            .swal-premium-title {
              font-size: 22px !important;
              font-weight: 850 !important;
              color: ${titleColor} !important;
              padding-top: 10px !important;
            }
            .swal-premium-confirm-btn {
              border-radius: 100px !important;
              padding: 12px 30px !important;
              font-weight: 700 !important;
              font-size: 14px !important;
              text-transform: none !important;
              box-shadow: 0 8px 20px rgba(42, 157, 143, 0.2) !important;
            }
            .swal-premium-cancel-btn {
              border-radius: 100px !important;
              padding: 12px 30px !important;
              font-weight: 700 !important;
              font-size: 14px !important;
              text-transform: none !important;
              background-color: ${cancelBgColor} !important;
              color: ${cancelTextColor} !important;
            }
          </style>
          <div style="text-align: center; padding: 10px 8px;">
            <p style="font-size: 15px; color: ${textColor}; line-height: 1.6; margin-bottom: 20px;">
              Bạn đã sử dụng hết <b>hạn mức học tập</b> của tháng này.<br>
              Vui lòng nạp thêm để tiếp tục trải nghiệm các tính năng phân tích và phản hồi thông minh cùng hệ thống!
            </p>
            <div style="background-color: ${infoBg}; border: 1px solid ${infoBorder}; border-radius: 12px; padding: 12px; font-size: 13px; color: ${infoTextColor}; font-weight: 600;">
              💡 Hạn ngạch tháng sẽ tự động làm mới dựa trên ngày đăng ký tài khoản của bạn.
            </div>
          </div>
        `,
        icon: "warning",
        iconColor: "#F59E0B",
        showCancelButton: true,
        cancelButtonText: "Để sau",
        confirmButtonText: "Nạp ngay",
        confirmButtonColor: "#2A9D8F",
        cancelButtonColor: cancelBgColor,
        customClass: {
          popup: "swal-premium-popup",
          title: "swal-premium-title",
          confirmButton: "swal-premium-confirm-btn",
          cancelButton: "swal-premium-cancel-btn",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/account";
        }
      });
    }
    return Promise.reject(error);
  }
);

const defaultHeader = (token?: string | null, isJson: boolean = true) => {
  const header: any = {
    "Cache-Control": "no-cache",
  };
  if (token) header.Authorization = `Bearer ${token}`;
  if (isJson) header["Content-Type"] = "application/json";
  return header;
};

class ApiRequests {
  public get<T = any>(apiUrl: string, params: any = {}, isAuth: boolean = true): Promise<T> {
    const headers = defaultHeader(isAuth ? localStorage.getItem(KeyTokenName) : null);
    return new Promise<T>((resolve, reject) => {
      axios
        .get(apiUrl, { headers, params })
        .then((response) => {
          const jsonData = response.data;
          resolve(jsonData);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public post<T = any>(
    apiUrl: string,
    input: any = {},
    isAuth: boolean = true,
    jsonParser: boolean = true
  ): Promise<T> {
    const isFormData = typeof FormData !== "undefined" && input instanceof FormData;
    const data = isFormData ? input : jsonParser ? JSON.stringify(input) : objectToFormData(input);
    const headers = defaultHeader(isAuth ? localStorage.getItem(KeyTokenName) : null, isFormData ? false : jsonParser);
    return new Promise<T>((resolve, reject) => {
      axios
        .post(apiUrl, data, {
          headers: { ...headers },
        })
        .then((response) => {
          const jsonData = response.data;
          resolve(jsonData);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public put<T = any>(apiUrl: string, input: any = {}, isAuth: boolean = true, jsonParser: boolean = true): Promise<T> {
    const isFormData = typeof FormData !== "undefined" && input instanceof FormData;
    const data = isFormData ? input : jsonParser ? JSON.stringify(input) : objectToFormData(input);
    const headers = defaultHeader(isAuth ? localStorage.getItem(KeyTokenName) : null, isFormData ? false : jsonParser);
    return new Promise<T>((resolve, reject) => {
      axios
        .put(apiUrl, data, {
          headers: { ...headers },
        })
        .then((response) => {
          const jsonData = response.data;
          resolve(jsonData);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public patch<T = any>(
    apiUrl: string,
    input: any = {},
    isAuth: boolean = true,
    jsonParser: boolean = true
  ): Promise<T> {
    const isFormData = typeof FormData !== "undefined" && input instanceof FormData;
    const data = isFormData ? input : jsonParser ? JSON.stringify(input) : objectToFormData(input);
    const headers = defaultHeader(isAuth ? localStorage.getItem(KeyTokenName) : null, isFormData ? false : jsonParser);
    return new Promise<T>((resolve, reject) => {
      axios
        .patch(apiUrl, data, {
          headers: { ...headers },
        })
        .then((response) => {
          const jsonData = response.data;
          resolve(jsonData);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public delete<T = any>(
    apiUrl: string,
    params: any = {},
    isAuth: boolean = true,
    jsonParser: boolean = false
  ): Promise<T> {
    const headers = defaultHeader(isAuth ? localStorage.getItem(KeyTokenName) : null, jsonParser);
    const data = jsonParser ? JSON.stringify(params) : objectToFormData(params);
    return new Promise<T>((resolve, reject) => {
      axios
        .delete(apiUrl, { headers, data })
        .then((response) => {
          const jsonData = response.data;
          resolve(jsonData);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }
}

export const Requests = new ApiRequests();
